import type { Rect } from './geometry';

export interface SpotlightRect extends Rect {
  radius: number;
}

function isHidden(el: Element): boolean {
  let node: Element | null = el;
  while (node) {
    const style = window.getComputedStyle(node);
    if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
      return true;
    }
    node = node.parentElement;
  }
  return false;
}

function isRendered(el: HTMLElement): boolean {
  if (el.closest('[data-onboarding-ready="false"]') || isHidden(el)) return false;
  const rect = el.getBoundingClientRect();
  return rect.width >= 2 && rect.height >= 2;
}

function taggedElements(id: string): HTMLElement[] {
  return Array.from(document.querySelectorAll<HTMLElement>(`[data-onboarding~="${id}"]`));
}

/**
 * Resolve the on-screen target for a `data-onboarding` id.
 * Desktop and mobile often mount the same control twice (one hidden by CSS) —
 * we pick the first copy that actually has a box.
 */
export function findOnboardingTargets(id: string): HTMLElement[] {
  return taggedElements(id).filter(isRendered);
}

export type OnboardingTargetState = 'visible' | 'hidden' | 'absent';

/**
 * Why a target isn't on screen, which is not the same question as whether it is.
 *
 * `ResponsiveTable` keeps both renderings mounted and lets CSS choose, so on a phone
 * the desktop table's column headers are present but `display: none`. That is this
 * layout's final answer — it will never show them — whereas an id matching nothing at
 * all usually means a page still filling in. The two deserve very different patience.
 */
export function onboardingTargetState(id: string): OnboardingTargetState {
  const all = taggedElements(id);
  if (all.length === 0) return 'absent';
  return all.some(isRendered) ? 'visible' : 'hidden';
}

export function findOnboardingTarget(id: string): HTMLElement | null {
  return findOnboardingTargets(id)[0] ?? null;
}

/**
 * A column filter that the tour holds open renders its panel above the overlay, so
 * it hides whatever the card or the arrow puts underneath it. Spotlighting the panel
 * along with its header makes the layout account for the space it really occupies.
 */
export function findOnboardingPanels(id: string): HTMLElement[] {
  return Array.from(
    document.querySelectorAll<HTMLElement>(`[data-onboarding-filter-panel="${id}"]`)
  ).filter(el => {
    if (el.closest('[data-onboarding-ready="false"]') || isHidden(el)) return false;
    const rect = el.getBoundingClientRect();
    return rect.width >= 2 && rect.height >= 2;
  });
}

export function measureSpotlight(el: HTMLElement, padding: number, radius: number): SpotlightRect {
  const rect = el.getBoundingClientRect();
  return {
    x: rect.left - padding,
    y: rect.top - padding,
    width: rect.width + padding * 2,
    height: rect.height + padding * 2,
    radius,
  };
}

export function unionSpotlight(
  els: HTMLElement[],
  padding: number,
  radius: number
): SpotlightRect {
  return unionRects(els.map(el => measureSpotlight(el, padding, radius)));
}

export function unionRects(rects: SpotlightRect[]): SpotlightRect {
  const left = Math.min(...rects.map(r => r.x));
  const top = Math.min(...rects.map(r => r.y));
  const right = Math.max(...rects.map(r => r.x + r.width));
  const bottom = Math.max(...rects.map(r => r.y + r.height));
  return {
    x: left,
    y: top,
    width: right - left,
    height: bottom - top,
    radius: rects[0]?.radius ?? 0,
  };
}

const RECT_EPSILON = 0.5;

export function spotlightRectsEqual(a: SpotlightRect | null, b: SpotlightRect | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    Math.abs(a.x - b.x) < RECT_EPSILON &&
    Math.abs(a.y - b.y) < RECT_EPSILON &&
    Math.abs(a.width - b.width) < RECT_EPSILON &&
    Math.abs(a.height - b.height) < RECT_EPSILON &&
    a.radius === b.radius
  );
}

export function spotlightListsEqual(a: SpotlightRect[], b: SpotlightRect[]): boolean {
  return a.length === b.length && a.every((rect, i) => spotlightRectsEqual(rect, b[i]));
}
